/**
 * Connector administration.
 *
 * The page states plainly that connectors are read-only, and the only actions
 * it offers are a reachability probe and an on-demand read (§4.8, §10).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { FreshnessBadge } from '../components/FreshnessBadge';
import { Notice } from '../components/Notice';
import { formatDateTime } from '../lib/format';
import { useApi } from '../lib/useApi';
import { useAuth } from '../lib/auth';

export function ConnectorsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const sources = useApi(() => api.dataSources(), []);
  const runs = useApi(() => api.ingestRuns(), []);
  const [message, setMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function probe(sourceId: string) {
    setBusyId(sourceId);
    setMessage(null);
    setErrorCode(null);
    try {
      const result = await api.probeDataSource(sourceId);
      setMessage(
        result.reachable
          ? t('admin.probeOk', { latency: result.latency_ms ?? '?' })
          : t('admin.probeFail', { detail: result.detail ?? '' }),
      );
    } catch (err) {
      setErrorCode((err as { code?: string }).code ?? 'server.error');
    } finally {
      setBusyId(null);
    }
  }

  async function poll(sourceId: string) {
    setBusyId(sourceId);
    setMessage(null);
    setErrorCode(null);
    try {
      const result = await api.pollDataSource(sourceId);
      setMessage(
        t('admin.pollDone', { samples: result.samples_written, devices: result.devices_seen }),
      );
      sources.reload();
      runs.reload();
    } catch (err) {
      setErrorCode((err as { code?: string }).code ?? 'server.error');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <h1>{t('admin.title')}</h1>
      <Notice tone="info">{t('admin.readOnlyNotice')}</Notice>
      <Notice tone="error" code={errorCode} />
      {message ? <Notice tone="info">{message}</Notice> : null}

      <div className="table-wrap">
        <table>
          <caption className="sr-only">{t('admin.title')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('device.name')}</th>
              <th scope="col">{t('admin.connector')}</th>
              <th scope="col">{t('admin.endpoint')}</th>
              <th scope="col" className="num">{t('admin.interval')}</th>
              <th scope="col">{t('device.status')}</th>
              <th scope="col">{t('admin.lastSuccess')}</th>
              <th scope="col" className="num">{t('admin.failures')}</th>
              <th scope="col" className="num">{t('kpi.devices')}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {(sources.data ?? []).map((source) => (
              <tr key={source.id}>
                <th scope="row">
                  {source.name}
                  {source.read_only ? <span className="badge badge--plain">read-only</span> : null}
                </th>
                <td>
                  {source.connector_type}
                  {source.firmware ? <span className="muted"> · FW {source.firmware}</span> : null}
                </td>
                <td className="mono break">{source.endpoint}</td>
                <td className="num">{source.poll_interval_seconds} s</td>
                <td>
                  <FreshnessBadge freshness={source.freshness} />
                </td>
                <td>{formatDateTime(source.last_success_at)}</td>
                <td className="num">{source.consecutive_failures}</td>
                <td className="num">{source.device_count}</td>
                <td className="row-actions">
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={busyId === source.id}
                    onClick={() => void probe(source.id)}
                  >
                    {t('admin.probe')}
                  </button>
                  {can('admin.sources.write') ? (
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={busyId === source.id}
                      onClick={() => void poll(source.id)}
                    >
                      {t('admin.poll')}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>{t('admin.runs')}</h2>
      <div className="table-wrap">
        <table>
          <caption className="sr-only">{t('admin.runs')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('incidents.opened')}</th>
              <th scope="col">{t('device.status')}</th>
              <th scope="col" className="num">{t('kpi.devices')}</th>
              <th scope="col" className="num">Samples</th>
              <th scope="col" className="num">{t('freshness.stale')}</th>
              <th scope="col" className="num">ms</th>
              <th scope="col">Error</th>
            </tr>
          </thead>
          <tbody>
            {(runs.data ?? []).slice(0, 25).map((run) => (
              <tr key={run.id}>
                <th scope="row">{formatDateTime(run.started_at)}</th>
                <td>{run.status}</td>
                <td className="num">{run.devices_seen}</td>
                <td className="num">{run.samples_written}</td>
                <td className="num">{run.stale_devices}</td>
                <td className="num">{run.duration_ms ?? '—'}</td>
                <td className="muted break">{run.error ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
